import gql from 'graphql-tag';

import { EDIT_EVENT_SESSIONS_FRAGMENT } from 'graphql/queries/events/edit-event';
import { EditableEvent, EventStatus } from 'types/event.types';

export const UPDATE_EVENT_DRAFT_MUTATION = gql`
  mutation updateEventDraft($id: Int!, $draftData: JSONString!) {
    updateEventDraft(id: $id, draftData: $draftData) {
      ok
    }
  }
`;

export interface EditEventMutationResult {
  editEvent: {
    event: EditableEvent;
  };
}

export interface EditMultiSessionEventMutationResult {
  editMultiSessionEvent: {
    event: EditableEvent;
  };
}

export const EDIT_MULTI_SESSION_EVENT_MUTATION = gql`
  mutation editMultiSessionEvent(
    $id: Int!
    $status: EventStatus
    $isPublished: Boolean
    $datePosted: String
    $title: String
    $description: String
    $photoUrl: String
    $originalPhotoUrl: String
    $photoXCrop: Int
    $photoYCrop: Int
    $photoZoom: Float
    $thumbnailUrl: String
    $slackPhotoUrl: String
    $hostOrganizationId: Int
    $contactName: String
    $contactEmail: String
    $tags: [Int]
    $sessions: [EventSessionInput]!
    $isVirtual: Boolean
    $isInPerson: Boolean
    $virtualLocation: String
    $companyLocationId: Int
    $physicalLocation: String
    $timezoneCode: String
    $allDay: Boolean
    $datetimeScheduled: String
    $datetimeEnd: String
    $hasUnlimitedSpots: Boolean
    $maxAttendees: Int
    $targetAttendance: Int
    $isPrivate: Boolean
    $isInviteOn: Boolean
    $clearAllPreviousInvitees: Boolean
    $addedInvitedUserIds: [Int]
    $removedInvitedUserIds: [Int]
    $distrolistInviteeIds: [Int]
    $listPrivacy: Int
    $createSlackChannel: Boolean
    $slackChannelId: String
    $slackChannelName: String
    $slackChannelDate: String
    $slackTeamId: String
    $slackChannelAutoarchive: Boolean
    $preeventEnable: Boolean
    $preEventUseTemplate: Boolean
    $preEventTemplateId: Int
    $preEventQuestions: [UpdateFeedbackQuestionInput]
    $feedbackEnable: Boolean
    $feedbackUseTemplate: Boolean
    $feedbackTemplateId: Int
    $feedbackSchedule: EventEndsRequestScheduleInput
    $feedbackQuestions: [UpdateFeedbackQuestionInput]
    $calendarNotifications: Boolean
    $slackNotifications: Boolean
    $emailNotifications: Boolean
    $sendCalendarInvites: Boolean
    $transparency: Int
    $sendSlackInvites: Boolean
    $updateNotificationOptions: EventUpdateNotificationOptionsInput
    $allowUserCheckin: Boolean
    $sendUserCheckinEmail: Boolean
    $sendUserCheckinSlack: Boolean
    $sendUserCheckinSchedule: EventBeginsRequestScheduleInput
    $registrationCutoffAt: String
    $questionnaireCutoffAt: String
    $budget: Int
    $slackReminders: [SlackReminderInput]
    $emailReminders: [EmailReminderInput]
    $limitRegistration: Boolean
    $t2DomainArray: [String]
  ) {
    editMultiSessionEvent(
      input: {
        id: $id
        companyLocationId: $companyLocationId
        status: $status
        isPublished: $isPublished
        datePosted: $datePosted
        title: $title
        description: $description
        photoUrl: $photoUrl
        originalPhotoUrl: $originalPhotoUrl
        photoXCrop: $photoXCrop
        photoYCrop: $photoYCrop
        photoZoom: $photoZoom
        thumbnailUrl: $thumbnailUrl
        slackPhotoUrl: $slackPhotoUrl
        hostOrganizationId: $hostOrganizationId
        contactName: $contactName
        contactEmail: $contactEmail
        tags: $tags
        sessions: $sessions
        isVirtual: $isVirtual
        virtualLocation: $virtualLocation
        preeventEnable: $preeventEnable
        feedbackEnable: $feedbackEnable
        preEventUseTemplate: $preEventUseTemplate
        preEventTemplateId: $preEventTemplateId
        preEventQuestions: $preEventQuestions
        feedbackUseTemplate: $feedbackUseTemplate
        feedbackTemplateId: $feedbackTemplateId
        feedbackSchedule: $feedbackSchedule
        feedbackQuestions: $feedbackQuestions
        isInPerson: $isInPerson
        physicalLocation: $physicalLocation
        timezoneCode: $timezoneCode
        allDay: $allDay
        datetimeScheduled: $datetimeScheduled
        datetimeEnd: $datetimeEnd
        registrationCutoffAt: $registrationCutoffAt
        questionnaireCutoffAt: $questionnaireCutoffAt
        isPrivate: $isPrivate
        isInviteOn: $isInviteOn
        clearAllPreviousInvitees: $clearAllPreviousInvitees
        addedInvitedUserIds: $addedInvitedUserIds
        removedInvitedUserIds: $removedInvitedUserIds
        distrolistInviteeIds: $distrolistInviteeIds
        listPrivacy: $listPrivacy
        createSlackChannel: $createSlackChannel
        slackChannelId: $slackChannelId
        slackChannelName: $slackChannelName
        slackChannelDate: $slackChannelDate
        slackTeamId: $slackTeamId
        slackChannelAutoarchive: $slackChannelAutoarchive
        calendarNotifications: $calendarNotifications
        slackNotifications: $slackNotifications
        emailNotifications: $emailNotifications
        maxAttendees: $maxAttendees
        targetAttendance: $targetAttendance
        hasUnlimitedSpots: $hasUnlimitedSpots
        sendCalendarInvites: $sendCalendarInvites
        transparency: $transparency
        sendSlackInvites: $sendSlackInvites
        updateNotificationOptions: $updateNotificationOptions
        allowUserCheckin: $allowUserCheckin
        sendUserCheckinEmail: $sendUserCheckinEmail
        sendUserCheckinSlack: $sendUserCheckinSlack
        sendUserCheckinSchedule: $sendUserCheckinSchedule
        budget: $budget
        slackReminders: $slackReminders
        emailReminders: $emailReminders
        limitRegistration: $limitRegistration
        t2DomainArray: $t2DomainArray
      }
    ) {
      event {
        id
        companyId
        companyLocationId
        status
        isPublished
        datePosted
        title
        description
        photoUrl
        originalPhotoUrl
        photoXCrop
        photoYCrop
        photoZoom
        thumbnailUrl
        slackPhotoUrl
        hostOrganizationId
        contactName
        contactEmail
        tags
        preeventEnable
        preEventUseTemplate
        preEventTemplateId
        feedbackEnable
        feedbackUseTemplate
        feedbackTemplateId
        feedbackSchedule {
          quantity
          unit
          beforeEventEnds
          sendAt
        }
        isVirtual
        virtualLocation
        isInPerson
        physicalLocation
        allDay
        timezoneCode
        datetimeScheduled
        datetimeEnd
        registrationCutoffAt
        questionnaireCutoffAt
        allowPlusOnes
        isPrivate
        isInviteOn
        listPrivacy
        createSlackChannel
        slackChannelId
        calendarNotifications
        slackNotifications
        emailNotifications
        slackChannelName
        slackChannelDate
        slackChannelAutoarchive
        ...EditEventSessionsFragment
        maxAttendees
        virtualEventInfo {
          hasUnlimitedSpots
        }
        userInviteeCount
        distrolistInviteeIds
        sendCalendarInvites
        transparency
        sendSlackInvites
        allowUserCheckin
        sendUserCheckinEmail
        sendUserCheckinSlack
        sendUserCheckinSchedule {
          quantity
          unit
          beforeEventBegins
        }
        preEventQuestions {
          id
          label
          type
          options
          hasOtherOption
          sort
          isRequired
          isDeleted
          shownBefore
          properties {
            scaleMinValue
            scaleMaxValue
            scaleMinLabel
            scaleMaxLabel
          }
        }
        postQuestions {
          id
          label
          type
          options
          hasOtherOption
          sort
          isRequired
          isDeleted
          shownBefore
          properties {
            scaleMinValue
            scaleMaxValue
            scaleMinLabel
            scaleMaxLabel
          }
        }
        budget
        limitRegistration
        t2DomainArray
        slackReminders {
          id
          message
          title
          datetime
          flagDelete
          notifyStatus
          schedule {
            quantity
            unit
            beforeEventBegins
          }
          selectedGuestStatuses
        }
        emailReminders {
          id
          subject
          message
          datetime
          flagDelete
          notifyStatus
          selectedGuestStatuses
          schedule {
            quantity
            unit
            beforeEventBegins
          }
        }
      }
    }
  }
  ${EDIT_EVENT_SESSIONS_FRAGMENT}
`;

export const EDIT_EVENT_MUTATION = gql`
  mutation editEvent(
    $id: Int!
    $status: EventStatus
    $isPublished: Boolean
    $datePosted: String
    $title: String
    $description: String
    $photoUrl: String
    $originalPhotoUrl: String
    $photoXCrop: Int
    $photoYCrop: Int
    $photoZoom: Float
    $thumbnailUrl: String
    $slackPhotoUrl: String
    $hostOrganizationId: Int
    $contactName: String
    $contactEmail: String
    $tags: [Int]
    $isVirtual: Boolean
    $isInPerson: Boolean
    $virtualLocation: String
    $companyLocationId: Int
    $physicalLocation: String
    $timezoneCode: String
    $allDay: Boolean
    $datetimeScheduled: String
    $datetimeEnd: String
    $hasUnlimitedSpots: Boolean
    $maxAttendees: Int
    $targetAttendance: Int
    $isPrivate: Boolean
    $isInviteOn: Boolean
    $clearAllPreviousInvitees: Boolean
    $addedInvitedUserIds: [Int]
    $removedInvitedUserIds: [Int]
    $distrolistInviteeIds: [Int]
    $listPrivacy: Int
    $createSlackChannel: Boolean
    $slackChannelId: String
    $slackChannelName: String
    $slackChannelDate: String
    $slackTeamId: String
    $slackChannelAutoarchive: Boolean
    $preeventEnable: Boolean
    $preEventUseTemplate: Boolean
    $preEventTemplateId: Int
    $preEventQuestions: [UpdateFeedbackQuestionInput]
    $feedbackEnable: Boolean
    $feedbackUseTemplate: Boolean
    $feedbackTemplateId: Int
    $feedbackSchedule: EventEndsRequestScheduleInput
    $feedbackQuestions: [UpdateFeedbackQuestionInput]
    $isFeedbackAnonymous: Boolean
    $calendarNotifications: Boolean
    $slackNotifications: Boolean
    $emailNotifications: Boolean
    $sendCalendarInvites: Boolean
    $transparency: Int
    $sendSlackInvites: Boolean
    $updateNotificationOptions: EventUpdateNotificationOptionsInput
    $allowUserCheckin: Boolean
    $sendUserCheckinEmail: Boolean
    $sendUserCheckinSlack: Boolean
    $sendUserCheckinSchedule: EventBeginsRequestScheduleInput
    $registrationCutoffAt: String
    $questionnaireCutoffAt: String
    $budget: Int
    $slackReminders: [SlackReminderInput]
    $emailReminders: [EmailReminderInput]
    $t2DomainArray: [String]
    $plusOneConfig: PlusOneConfigInput
  ) {
    editEvent(
      input: {
        id: $id
        companyLocationId: $companyLocationId
        status: $status
        isPublished: $isPublished
        datePosted: $datePosted
        title: $title
        description: $description
        photoUrl: $photoUrl
        originalPhotoUrl: $originalPhotoUrl
        photoXCrop: $photoXCrop
        photoYCrop: $photoYCrop
        photoZoom: $photoZoom
        thumbnailUrl: $thumbnailUrl
        slackPhotoUrl: $slackPhotoUrl
        hostOrganizationId: $hostOrganizationId
        contactName: $contactName
        contactEmail: $contactEmail
        tags: $tags
        isVirtual: $isVirtual
        virtualLocation: $virtualLocation
        preeventEnable: $preeventEnable
        feedbackEnable: $feedbackEnable
        preEventUseTemplate: $preEventUseTemplate
        preEventTemplateId: $preEventTemplateId
        preEventQuestions: $preEventQuestions
        feedbackUseTemplate: $feedbackUseTemplate
        feedbackTemplateId: $feedbackTemplateId
        feedbackSchedule: $feedbackSchedule
        feedbackQuestions: $feedbackQuestions
        isFeedbackAnonymous: $isFeedbackAnonymous
        isInPerson: $isInPerson
        physicalLocation: $physicalLocation
        timezoneCode: $timezoneCode
        allDay: $allDay
        datetimeScheduled: $datetimeScheduled
        datetimeEnd: $datetimeEnd
        registrationCutoffAt: $registrationCutoffAt
        questionnaireCutoffAt: $questionnaireCutoffAt
        isPrivate: $isPrivate
        isInviteOn: $isInviteOn
        clearAllPreviousInvitees: $clearAllPreviousInvitees
        addedInvitedUserIds: $addedInvitedUserIds
        removedInvitedUserIds: $removedInvitedUserIds
        distrolistInviteeIds: $distrolistInviteeIds
        listPrivacy: $listPrivacy
        createSlackChannel: $createSlackChannel
        slackChannelId: $slackChannelId
        slackChannelName: $slackChannelName
        slackChannelDate: $slackChannelDate
        slackTeamId: $slackTeamId
        slackChannelAutoarchive: $slackChannelAutoarchive
        calendarNotifications: $calendarNotifications
        slackNotifications: $slackNotifications
        emailNotifications: $emailNotifications
        maxAttendees: $maxAttendees
        targetAttendance: $targetAttendance
        hasUnlimitedSpots: $hasUnlimitedSpots
        sendCalendarInvites: $sendCalendarInvites
        transparency: $transparency
        sendSlackInvites: $sendSlackInvites
        updateNotificationOptions: $updateNotificationOptions
        allowUserCheckin: $allowUserCheckin
        sendUserCheckinEmail: $sendUserCheckinEmail
        sendUserCheckinSlack: $sendUserCheckinSlack
        sendUserCheckinSchedule: $sendUserCheckinSchedule
        budget: $budget
        slackReminders: $slackReminders
        emailReminders: $emailReminders
        t2DomainArray: $t2DomainArray
        plusOneConfig: $plusOneConfig
      }
    ) {
      event {
        id
        companyId
        companyLocationId
        status
        isPublished
        datePosted
        title
        description
        photoUrl
        originalPhotoUrl
        photoXCrop
        photoYCrop
        photoZoom
        thumbnailUrl
        slackPhotoUrl
        hostOrganizationId
        contactName
        contactEmail
        tags
        preeventEnable
        preEventUseTemplate
        preEventTemplateId
        feedbackEnable
        feedbackUseTemplate
        feedbackTemplateId
        feedbackSchedule {
          quantity
          unit
          beforeEventEnds
          sendAt
        }
        isFeedbackAnonymous
        isVirtual
        virtualLocation
        isInPerson
        physicalLocation
        allDay
        timezoneCode
        datetimeScheduled
        datetimeEnd
        registrationCutoffAt
        questionnaireCutoffAt
        allowPlusOnes
        plusOneConfig {
          allowPlusOnes
          maxPlusOnes
          collectQuestionnaireResponses
          maxRegisteredPlusOnes
        }
        isPrivate
        isInviteOn
        listPrivacy
        createSlackChannel
        slackChannelId
        calendarNotifications
        slackNotifications
        emailNotifications
        slackChannelName
        slackChannelDate
        slackChannelAutoarchive
        maxAttendees
        targetAttendance
        virtualEventInfo {
          hasUnlimitedSpots
        }
        userInviteeCount
        distrolistInviteeIds
        sendCalendarInvites
        transparency
        sendSlackInvites
        allowUserCheckin
        slackReminders {
          id
          message
          title
          datetime
          notifyStatus
          schedule {
            quantity
            unit
            beforeEventBegins
          }
          selectedGuestStatuses
        }
        emailReminders {
          id
          subject
          message
          datetime
          notifyStatus
          selectedGuestStatuses
          schedule {
            quantity
            unit
            beforeEventBegins
          }
        }
        sendUserCheckinEmail
        sendUserCheckinSlack
        sendUserCheckinSchedule {
          quantity
          unit
          beforeEventBegins
        }
        preEventQuestions {
          id
          label
          type
          options
          hasOtherOption
          sort
          isRequired
          isDeleted
          shownBefore
          properties {
            scaleMinValue
            scaleMaxValue
            scaleMinLabel
            scaleMaxLabel
          }
        }
        postQuestions {
          id
          label
          type
          options
          hasOtherOption
          sort
          isRequired
          isDeleted
          shownBefore
          properties {
            scaleMinValue
            scaleMaxValue
            scaleMinLabel
            scaleMaxLabel
          }
        }
        budget
        slackReminders {
          id
          message
          title
          datetime
          flagDelete
          notifyStatus
          schedule {
            quantity
            unit
            beforeEventBegins
          }
          selectedGuestStatuses
        }
        emailReminders {
          id
          subject
          message
          datetime
          flagDelete
          notifyStatus
          selectedGuestStatuses
          schedule {
            quantity
            unit
            beforeEventBegins
          }
        }
        t2DomainArray
      }
    }
  }
`;

export interface EditEventStatusResult {
  event: {
    id: number;
    status: EventStatus;
  };
}

export interface EditEventStatusVariables {
  id: number;
  status: EventStatus;
}

export interface EventSessionPayload {
  id?: number;
  title?: string;
  description?: string;
  datetimeScheduled?: string;
  datetimeEnd?: string;
  allDay?: boolean;
  isInPerson?: boolean;
  companyLocationId?: number;
  locationAddress?: string;
  isVirtual?: boolean;
  virtualLocation?: string;
}

export const EDIT_EVENT_STATUS_MUTATION = gql`
  mutation editEventStatus($id: Int!, $status: EventStatus!) {
    editEventStatus(input: { id: $id, status: $status }) {
      event {
        id
        status
      }
    }
  }
`;

export interface RemoveZoomLinkMutationResult {
  removeZoomLink: {
    ok: boolean;
  };
}

export interface RemoveZoomLinkMutationVariables {
  eventId?: number;
  zoomMeetingId?: number;
}

export interface RemoveMeetLinkMutationResult {
  removeMeetLink: {
    ok: boolean;
  };
}

export interface RemoveMeetLinkMutationVariables {
  eventId?: number;
  googleMeetingId?: number;
}

export const CREATE_MEET_LINK = gql`
  mutation createMeetLink($eventId: Int) {
    createMeetLink(eventId: $eventId) {
      ok
      meetLink
      googleMeetingId
    }
  }
`;

export const REMOVE_MEET_LINK = gql`
  mutation removeMeetLink($eventId: Int, $googleMeetingId: Int) {
    removeMeetLink(eventId: $eventId, googleMeetingId: $googleMeetingId) {
      ok
    }
  }
`;

export const CREATE_ZOOM_LINK = gql`
  mutation createZoomLink(
    $eventId: Int
    $title: String
    $description: String
    $datetimeScheduled: String
    $datetimeEnd: String
    $timezoneCode: String
  ) {
    createZoomLink(
      input: {
        eventId: $eventId
        title: $title
        description: $description
        datetimeScheduled: $datetimeScheduled
        datetimeEnd: $datetimeEnd
        timezoneCode: $timezoneCode
      }
    ) {
      link
      ok
      zoomMeetingId
    }
  }
`;

export const REMOVE_ZOOM_LINK = gql`
  mutation removeZoomLink($eventId: Int, $zoomMeetingId: Int) {
    removeZoomLink(input: { eventId: $eventId, zoomMeetingId: $zoomMeetingId }) {
      ok
    }
  }
`;

export const GENERATE_ZOOM_LINK = gql`
  mutation generateZoomLink(
    $eventId: Int!
    $zoomAccountId: Int!
    $timezoneCode: String!
    $title: String!
    $description: String!
    $datetimeScheduled: String!
    $datetimeEnd: String!
  ) {
    generateZoomLink(
      input: {
        eventId: $eventId
        zoomAccountId: $zoomAccountId
        timezoneCode: $timezoneCode
        title: $title
        description: $description
        datetimeScheduled: $datetimeScheduled
        datetimeEnd: $datetimeEnd
      }
    ) {
      link
    }
  }
`;
